import { useInfiniteQuery, useMutation, useQuery } from 'react-query';
import { CurrencyEnum, LanguageEnum } from '@distribusion/portal-lib/constants';
import { queryClient } from '@distribusion/portal-lib/query-client';
import { PaginatedListApiResponse, UpdateApiResponse } from '@distribusion/portal-lib/types';
import { message } from 'antd';
import { USER_MANAGEMENT_BASE_API_PATH } from 'constants/common';
import { AccessTypeEnum, SortingOptionsEnum, UserStateEnum } from 'features/users/constants';
import axios, { AxiosError } from 'lib/axios';
import { useTranslation } from 'lib/translation';

export interface Localization {
  timezone?: string;
  language?: LanguageEnum;
  currency?: CurrencyEnum;
  currency_options?: CurrencyEnum[];
}

export interface User {
  id: string;
  access_type: AccessTypeEnum;
  state: UserStateEnum;
  demo_mode: boolean;
  first_name: string;
  last_name: string;
  full_name: string;
  email: string;
  phone: string;
  language: LanguageEnum;
  timezone: string;
  currency: CurrencyEnum;
  currency_options: CurrencyEnum[];
  created_at: string;
  updated_at: string;
}

type Id = Pick<User, 'id'>;

export interface GetUsersRequestParams {
  query?: string;
  state?: UserStateEnum;
  access_type?: AccessTypeEnum;
  email?: string;
  sorting?: SortingOptionsEnum;
  page_size?: number;
  page?: number;
}

interface GetUsersResponse extends PaginatedListApiResponse<User> {
  actions: {
    create: boolean;
  };
}

interface SendRecoveryLinkResponse {
  recovery_link: string;
  expires_at: string;
}

export interface GetUserResponse {
  data: User;
  actions: {
    update_personal_info: boolean;
    update_localization_preferences: boolean;
    update_state: boolean;
    update_access_type: boolean;
    update_permissions: boolean;
    switch_demo_mode: boolean;
  };
}

interface CreateUserConfig extends Omit<User, 'id' | 'full_name' | 'created_at' | 'updated_at'> {
  organizations: number[];
}

interface SetUserStateConfig {
  id: string;
  state: UserStateEnum;
}

export const usersKeys = {
  all: ['root', 'users'] as const,
  list: (params: GetUsersRequestParams) => [...usersKeys.all, 'list', params] as const,
  detail: (id: string) => [...usersKeys.all, 'detail', id] as const,
};

const getUsers = async (params?: GetUsersRequestParams) => {
  const { data } = await axios.get<GetUsersResponse>(`${USER_MANAGEMENT_BASE_API_PATH}/v2/users/`, {
    params,
  });
  return data;
};

const getUserById = async (id: string) => {
  const { data } = await axios.get<GetUserResponse>(
    `${USER_MANAGEMENT_BASE_API_PATH}/v2/users/${id}`,
  );
  return data;
};

const createUser = async (userConfig: CreateUserConfig) => {
  const { data } = await axios.post<GetUserResponse>(
    `${USER_MANAGEMENT_BASE_API_PATH}/v2/users`,
    userConfig,
  );
  return data;
};

type UserInfo = Pick<User, 'first_name' | 'last_name' | 'phone'>;

const updateUserInfo = async ({ id, ...userInfo }: Id & UserInfo) => {
  const { data } = await axios.patch<UpdateApiResponse>(
    `${USER_MANAGEMENT_BASE_API_PATH}/v1/users/${id}`,
    userInfo,
  );
  return data;
};

const updateUserPreferences = async ({ id, ...localization }: Id & Localization) => {
  const { data } = await axios.patch(
    `${USER_MANAGEMENT_BASE_API_PATH}/v1/users/${id}/preferences`,
    localization,
  );
  return data;
};

const setUserDemoMode = async ({ id, demo_mode }: Id & { demo_mode: boolean }) => {
  const { data } = await axios.patch<UpdateApiResponse>(
    `${USER_MANAGEMENT_BASE_API_PATH}/v1/users/${id}/mode`,
    { demo_mode },
  );
  return data;
};

const setUserState = async ({ id, state }: SetUserStateConfig) => {
  const { data } = await axios.post<UpdateApiResponse>(
    `${USER_MANAGEMENT_BASE_API_PATH}/v1/users/${id}/state`,
    { state },
  );
  return data;
};

const sendRecoveryLink = async (id: string) => {
  const { data } = await axios.post<SendRecoveryLinkResponse>(
    `${USER_MANAGEMENT_BASE_API_PATH}/v1/users/${id}/recovery`,
  );
  return data;
};

export const useGetUserById = (id: string, options?: { enabled: boolean }) => {
  return useQuery<GetUserResponse, AxiosError>(usersKeys.detail(id), () => getUserById(id), {
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: options?.enabled,
  });
};

export const useGetUsers = (params: GetUsersRequestParams) =>
  useInfiniteQuery<GetUsersResponse, AxiosError>(
    usersKeys.list(params),
    ({ pageParam: { page, pageSize } = { page: 1, pageSize: 10 } }) =>
      getUsers({ ...params, page, page_size: pageSize }),
    {
      keepPreviousData: true,
      retry: false,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    },
  );

export const useSendRecoveryLink = () => {
  const { t } = useTranslation();
  return useMutation(sendRecoveryLink, {
    onError: () => {
      message.error(t('users.errors.an_error_occurred'));
    },
  });
};

export const useCreateUser = () => {
  const { t } = useTranslation();
  return useMutation(createUser, {
    onSuccess: () => {
      message.success(t('users.messages.user_created'));
    },
  });
};

export const useUpdateUserInfo = () => {
  const { t } = useTranslation();
  return useMutation(updateUserInfo, {
    onError: () => {
      message.error(t('users.errors.failed_to_change_user_info'));
    },
  });
};

export const useUpdateUserPreferences = () => {
  const { t } = useTranslation();
  return useMutation(updateUserPreferences, {
    onError: () => {
      message.error(t('users.errors.failed_to_change_user_info'));
    },
  });
};

export const useSetUserDemoMode = () => {
  const { t } = useTranslation();
  return useMutation(setUserDemoMode, {
    onError: () => {
      message.error(t('users.errors.failed_to_change_demo_mode'));
    },
  });
};

export const useSetUserState = () => {
  const { t } = useTranslation();
  return useMutation(setUserState, {
    onSuccess: ({ success }, { id }) => {
      if (success) {
        queryClient.invalidateQueries(usersKeys.detail(id));
      } else {
        message.error(t('users.errors.failed_to_set_state'));
      }
    },
    onError: () => {
      message.error(t('users.errors.failed_to_set_state'));
    },
  });
};
